import React, { useReducer, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PageContent, Container, Row, Col, Field, Heading, Button, Datepicker } from 'components';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  updateIpmaCisCreator,
  setIpmaCisEditErrorsCreator,
} from '../../../../../store/actionsCreators';
import moment from 'moment';
import reducer, { defaultState, updateFieldCreator, setEditDataCreator } from './reducer';

const EditLevelDomain = ({ errors, levelDomain, updateLevelDomain, removeError }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const history = useHistory();

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeDatepicker = (name, errorName) => (date) => {
    dispatch(updateFieldCreator({ name, value: date }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const submit = () => {
    const params = {
      level: state.level,
      domain: state.domain,
      designation: state.designation,
      validFrom: state.validFrom ? moment(state.validFrom).format('YYYY-MM-DD') : null,
      validTo: state.validTo ? moment(state.validTo).format('YYYY-MM-DD') : null,
    };

    updateLevelDomain(state.id, params, history);
  };

  useEffect(() => {
    if (levelDomain) {
      dispatch(setEditDataCreator(levelDomain));
    }
  }, [levelDomain]);

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.levelDomains.editLevelDomain')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="level"
                  label={t('page.user.cis.levelDomains.level')}
                  required
                  error={errors.level}
                >
                  <input
                    id="level"
                    type="text"
                    placeholder={t('page.user.cis.levelDomains.levelPlaceholder')}
                    value={state.level}
                    onChange={handleChangeNative('level', 'level')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="domain"
                  label={t('page.user.cis.levelDomains.domain')}
                  required
                  error={errors.domain}
                >
                  <input
                    id="domain"
                    type="text"
                    placeholder={t('page.user.cis.levelDomains.domainPlaceholder')}
                    value={state.domain}
                    onChange={handleChangeNative('domain', 'domain')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="designation"
                  label={t('page.user.cis.levelDomains.designation')}
                  required
                  error={errors.designation}
                >
                  <input
                    id="designation"
                    type="text"
                    placeholder={t('page.user.cis.levelDomains.designationPlaceholder')}
                    value={state.designation}
                    onChange={handleChangeNative('designation', 'designation')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="validFrom"
                  label={t('page.user.cis.levelDomains.validFrom')}
                  error={errors.validFrom}
                >
                  <Datepicker
                    id="validFrom"
                    selected={state.validFrom}
                    onChange={handleChangeDatepicker('validFrom', 'validFrom')}
                    placeholderText={t('page.user.cis.levelDomains.validFromPlaceholder')}
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="validTo"
                  label={t('page.user.cis.levelDomains.validTo')}
                  error={errors.validTo}
                >
                  <Datepicker
                    id="validTo"
                    selected={state.validTo}
                    onChange={handleChangeDatepicker('validTo', 'validTo')}
                    placeholderText={t('page.user.cis.levelDomains.validToPlaceholder')}
                    showYearDropdown
                    showMonthDropdown
                    minDate={new Date(state.validFrom)}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

EditLevelDomain.propTypes = {
  deltaCertificate: PropTypes.shape({
    id: PropTypes.number,
    level: PropTypes.string,
    domain: PropTypes.string,
    designation: PropTypes.string,
    validFrom: PropTypes.any,
    validTo: PropTypes.any,
  }),
  errors: PropTypes.shape({
    level: PropTypes.string,
    domain: PropTypes.string,
    designation: PropTypes.string,
    validFrom: PropTypes.string,
    validTo: PropTypes.string,
  }),
  updateLevelDomain: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
};

EditLevelDomain.defaultProps = {
  levelDomain: null,
  errors: {},
};

const mapStateToProps = ({ ipma }) => ({
  levelDomain: ipma.cis.levelDomains.profile,
  errors: ipma.cis.levelDomains.editErrors,
});

const mapDispatchToProps = (dispatch) => ({
  updateLevelDomain: (id, params, history) =>
    dispatch(updateIpmaCisCreator(id, 'levelDomains', params, history)),
  removeError: (errors) => dispatch(setIpmaCisEditErrorsCreator(errors)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditLevelDomain);
